import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
    Box,
    Collapse,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Popper,
    Slide,
    Tooltip,
    Typography,
} from "@mui/material";
import PropTypes from "prop-types";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import { identifiersData } from "@/data";
import { sidebarItemHeight, StyledSidebarChildDot, StyledSidebarFlyout, StyledSidebarFlyoutClip } from "@/styles";

// Moving the pointer from the rail to the flyout crosses a gap, so the close is deferred long enough to survive it.
const flyoutCloseDelay = 120;

const SidebarChildTab = ({ activeTab, compact, onNavigate, tab }) => {
    const { t } = useTranslation();

    const {
        classNames: { active: activeClassName, sidebarChild: childClassName },
        ids: { button: buttonId, sidebar: sidebarId },
    } = identifiersData;

    const { id, key, routeUrl } = tab;

    return (
        <ListItem sx={{ display: "block" }} disablePadding>
            <ListItemButton
                className={`${childClassName}${activeTab?.id === id ? ` ${activeClassName}` : ""}`}
                component={Link}
                id={`${sidebarId}_${key}_${buttonId}`}
                to={routeUrl}
                nativeButton={false}
                sx={{
                    minHeight: 36,
                    px: 2.5,
                }}
                onClick={onNavigate}
            >
                {/* The dot both marks the row as a child and holds the indent, so no extra padding is needed.
                    Inline, the gap matches the parent's so the two labels share a start edge; in the flyout there
                    is no parent icon to line up with, so it closes to just enough to separate dot from label. */}
                <ListItemIcon sx={{ minWidth: 0, mr: compact ? 1 : 3 }}>
                    <StyledSidebarChildDot />
                </ListItemIcon>
                <ListItemText primary={t(`${key}.name`)} slotProps={{ primary: { sx: { fontSize: "0.8rem" } } }} />
            </ListItemButton>
        </ListItem>
    );
};

SidebarChildTab.propTypes = {
    activeTab: PropTypes.shape({ id: PropTypes.number }),
    compact: PropTypes.bool,
    onNavigate: PropTypes.func,
    tab: PropTypes.shape({
        id: PropTypes.number,
        key: PropTypes.string,
        routeUrl: PropTypes.string,
    }),
};

const SidebarTab = ({ activeTab, expanded, onToggle, open, setOpen, tab }) => {
    const { t } = useTranslation();

    const {
        classNames: { active: activeClassName, sidebarFlyoutOpen: flyoutOpenClassName },
        ids: { button: buttonId, menu: menuId, sidebar: sidebarId },
    } = identifiersData;

    const { Icon, children, id, key, routeUrl } = tab;

    const hasChildren = children?.length > 0;

    const isActive = activeTab?.id === id;

    const holdsActiveChild = activeTab?.parentId === id;

    const isHighlighted = isActive || (!open && holdsActiveChild);

    // The anchor outlives the open state: Popper still positions itself while sliding back in, so clearing the
    // anchor on close would tear the panel to the corner mid-exit.
    const [flyoutAnchor, setFlyoutAnchor] = useState(null);

    const [flyoutOpen, setFlyoutOpen] = useState(false);

    const closeTimeoutRef = useRef(null);

    const clipRef = useRef(null);

    // A flyout only earns its place while the rail is collapsed, where the inline children would be unreadable.
    const hasFlyout = hasChildren && !open;

    const cancelFlyoutClose = () => {
        if (!closeTimeoutRef.current) return;

        clearTimeout(closeTimeoutRef.current);

        closeTimeoutRef.current = null;
    };

    const openFlyout = (event) => {
        if (!hasFlyout) return;

        cancelFlyoutClose();

        setFlyoutAnchor(event.currentTarget);

        setFlyoutOpen(true);
    };

    const closeFlyout = (immediately) => {
        cancelFlyoutClose();

        if (immediately) {
            setFlyoutOpen(false);

            return;
        }

        closeTimeoutRef.current = setTimeout(() => setFlyoutOpen(false), flyoutCloseDelay);
    };

    // The rail can be expanded (or the tab unmounted by a permission change) while a close is still pending.
    useEffect(() => {
        if (!hasFlyout) closeFlyout(true);

        return cancelFlyoutClose;
    }, [hasFlyout]); // eslint-disable-line

    const linkProps = routeUrl
        ? {
              component: Link,
              nativeButton: false,
              to: routeUrl,
          }
        : {};

    const handleParentClick = () => {
        if (!hasChildren) return;

        // Collapsed, the children live in the flyout, so a click on a route-less parent opens the rail to reach them.
        if (!open) setOpen(true);

        onToggle(id);
    };

    return (
        <>
            <Tooltip disableHoverListener={hasFlyout} placement="right" title={t(`${key}.name`)}>
                <ListItem
                    sx={{ display: "block" }}
                    disablePadding
                    onMouseEnter={openFlyout}
                    onMouseLeave={() => closeFlyout()}
                >
                    <ListItemButton
                        aria-expanded={hasChildren ? (hasFlyout ? flyoutOpen : expanded) : undefined}
                        aria-haspopup={hasFlyout ? "menu" : undefined}
                        // The pointer leaves this row to reach the flyout, so the open state — not `:hover` —
                        // is what keeps the parent lit while its own menu is up.
                        className={[isHighlighted && activeClassName, flyoutOpen && flyoutOpenClassName]
                            .filter(Boolean)
                            .join(" ")}
                        id={`${sidebarId}_${key}_${buttonId}`}
                        {...linkProps}
                        sx={{
                            justifyContent: open ? "initial" : "center",
                            minHeight: sidebarItemHeight,
                            px: 2.5,
                        }}
                        onBlur={() => closeFlyout()}
                        onClick={routeUrl ? undefined : handleParentClick}
                        onFocus={openFlyout}
                    >
                        <ListItemIcon
                            sx={{
                                justifyContent: "center",
                                minWidth: 0,
                                mr: open ? 3 : "auto",
                            }}
                        >
                            <Icon color={isHighlighted ? "primary" : ""} />
                        </ListItemIcon>
                        <ListItemText primary={t(`${key}.name`)} sx={{ opacity: open ? 1 : 0 }} />
                        {hasChildren ? (
                            <Box
                                aria-label={t(expanded ? "actions.collapse" : "actions.expand")}
                                component="span"
                                id={`${sidebarId}_${key}_toggle_${buttonId}`}
                                role="button"
                                tabIndex={0}
                                sx={{
                                    display: open ? "flex" : "none",
                                    ml: 1,
                                }}
                                onClick={(event) => {
                                    // The row itself is a link, so the chevron owns the expansion on its own.
                                    event.preventDefault();

                                    event.stopPropagation();

                                    handleParentClick();
                                }}
                                onKeyDown={(event) => {
                                    if (event.key !== "Enter" && event.key !== " ") return;

                                    event.preventDefault();

                                    event.stopPropagation();

                                    handleParentClick();
                                }}
                            >
                                {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                            </Box>
                        ) : null}
                    </ListItemButton>
                </ListItem>
            </Tooltip>
            {hasChildren ? (
                <Collapse in={open && expanded} timeout="auto" unmountOnExit>
                    <List disablePadding>
                        {children.map((child) => (
                            <SidebarChildTab key={child.id} activeTab={activeTab} tab={child} />
                        ))}
                    </List>
                </Collapse>
            ) : null}
            {hasFlyout ? (
                <Popper
                    anchorEl={flyoutAnchor}
                    id={`${sidebarId}_${key}_${menuId}`}
                    open={flyoutOpen}
                    placement="right-start"
                    modifiers={[
                        // Flush against the rail, so the two surfaces read as one.
                        {
                            name: "offset",
                            options: { offset: [0, 0] },
                        },
                        // Sliding the flyout along the rail would break the seam, so it flips instead of shifting.
                        {
                            name: "preventOverflow",
                            options: { altAxis: true, mainAxis: false },
                        },
                    ]}
                    sx={{ zIndex: (muiTheme) => muiTheme?.zIndex?.drawer + 1 }}
                    transition
                    onKeyDown={(event) => event.key === "Escape" && closeFlyout(true)}
                    onMouseEnter={cancelFlyoutClose}
                    onMouseLeave={() => closeFlyout()}
                >
                    {({ TransitionProps }) => (
                        <StyledSidebarFlyoutClip ref={clipRef}>
                            {/* The clip is the panel's own bounding box, so `right` travels exactly its width. */}
                            <Slide
                                {...TransitionProps}
                                container={() => clipRef.current}
                                direction="right"
                                timeout={{ enter: 220, exit: 180 }}
                            >
                                <StyledSidebarFlyout elevation={0}>
                                    <Box component="header">
                                        {routeUrl ? (
                                            <ListItemButton
                                                className={isActive ? activeClassName : ""}
                                                component={Link}
                                                id={`${sidebarId}_${key}_flyoutTitle_${buttonId}`}
                                                to={routeUrl}
                                                nativeButton={false}
                                                sx={{
                                                    height: "100%",
                                                    px: 2.5,
                                                    width: "100%",
                                                    // The strip already carries the header tint; a default hover
                                                    // overlay on top of it just looks muddy, so this stays flat.
                                                    "&:hover": { backgroundColor: "transparent" },
                                                }}
                                                onClick={() => closeFlyout(true)}
                                            >
                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        fontSize: "0.9rem",
                                                        marginTop: 0.75,
                                                        fontWeight: 600,
                                                        height: "100%",
                                                    }}
                                                >
                                                    {t(`${key}.name`)}
                                                </Typography>
                                            </ListItemButton>
                                        ) : (
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    alignItems: "center",
                                                    display: "flex",
                                                    fontWeight: 600,
                                                    height: "100%",
                                                    px: 2.5,
                                                }}
                                            >
                                                {t(`${key}.name`)}
                                            </Typography>
                                        )}
                                    </Box>
                                    <List disablePadding>
                                        {children.map((child) => (
                                            <SidebarChildTab
                                                key={child.id}
                                                activeTab={activeTab}
                                                tab={child}
                                                compact
                                                onNavigate={() => closeFlyout(true)}
                                            />
                                        ))}
                                    </List>
                                </StyledSidebarFlyout>
                            </Slide>
                        </StyledSidebarFlyoutClip>
                    )}
                </Popper>
            ) : null}
        </>
    );
};

export default SidebarTab;

SidebarTab.propTypes = {
    activeTab: PropTypes.shape({
        id: PropTypes.number,
        parentId: PropTypes.number,
    }),
    expanded: PropTypes.bool,
    onToggle: PropTypes.func,
    open: PropTypes.bool,
    setOpen: PropTypes.func,
    tab: PropTypes.shape({
        Icon: PropTypes.elementType,
        children: PropTypes.arrayOf(
            PropTypes.shape({
                id: PropTypes.number,
                key: PropTypes.string,
                routeUrl: PropTypes.string,
            }),
        ),
        id: PropTypes.number,
        key: PropTypes.string,
        routeUrl: PropTypes.string,
    }),
};
